import { useMemo, useState } from 'react'
import {
  Alert,
  App,
  Badge,
  Button,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { bindingsApi } from '@/api/bindings'
import {
  INDEX_KIND_LABELS,
  type Drawing,
  type IndexKind,
  type PlanIndexRef,
  type PlanTargetBinding,
  type UUID,
} from '@/types/api'

const { Text } = Typography

interface Props {
  jobId: UUID
  open: boolean
  onClose: () => void
}

const KIND_TABS: (IndexKind | 'all')[] = [
  'all',
  'elevation',
  'detail',
  'section',
  'opening',
  'equipment',
]

const DRAWING_ROLE_LABELS: Record<string, string> = {
  primary_plan: '主平面',
  unused_plan: '备用平面',
  elevation: '立面',
  ceiling: '天花',
  section: '剖面',
  detail: '大样',
  schedule: '门窗表',
  other: '其它',
}

const DRAWING_ROLE_COLORS: Record<string, string> = {
  primary_plan: 'geekblue',
  elevation: 'green',
  ceiling: 'cyan',
  section: 'gold',
  detail: 'orange',
  schedule: 'purple',
  unused_plan: 'default',
  other: 'default',
}

export function RelatedDrawingsDrawer({ jobId, open, onClose }: Props) {
  const qc = useQueryClient()
  const { message } = App.useApp()

  const [tab, setTab] = useState<IndexKind | 'all'>('all')
  const [editingRef, setEditingRef] = useState<PlanIndexRef | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  // ---- 数据 ----
  const { data: drawingSet } = useQuery({
    queryKey: ['drawing-set', jobId],
    queryFn: () => bindingsApi.drawingSet(jobId),
    enabled: open,
    retry: false,
  })
  const { data: refsSet } = useQuery({
    queryKey: ['plan-index-refs', jobId],
    queryFn: () => bindingsApi.refs(jobId),
    enabled: open,
    retry: false,
  })
  const { data: bindingSet } = useQuery({
    queryKey: ['drawing-binding', jobId],
    queryFn: () => bindingsApi.binding(jobId),
    enabled: open,
    retry: false,
  })

  // ref_id → binding
  const bindingByRef = useMemo(() => {
    const m: Record<string, PlanTargetBinding> = {}
    bindingSet?.bindings.forEach((b) => (m[b.ref_id] = b))
    return m
  }, [bindingSet])

  // 过滤 tab
  const filteredRefs = useMemo(() => {
    const list = refsSet?.refs || []
    if (tab === 'all') return list
    return list.filter((r) => r.kind === tab)
  }, [refsSet, tab])

  // ---- 数据统计（tab badge 数量） ----
  const countByKind = useMemo(() => {
    const c: Record<string, number> = { all: refsSet?.refs.length || 0 }
    refsSet?.refs.forEach((r) => {
      c[r.kind] = (c[r.kind] || 0) + 1
    })
    return c
  }, [refsSet])

  // ---- mutations ----
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['plan-index-refs', jobId] })
    qc.invalidateQueries({ queryKey: ['drawing-binding', jobId] })
  }

  const upsertMutation = useMutation({
    mutationFn: bindingsApi.upsert,
    onSuccess: () => {
      message.success('索引已保存并重新绑定')
      invalidate()
      setModalOpen(false)
      setEditingRef(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (refId: UUID) => bindingsApi.remove(jobId, refId),
    onSuccess: () => {
      message.success('索引已删除')
      invalidate()
    },
  })

  const notReady = !refsSet && !drawingSet
  const stats = bindingSet

  return (
    <Drawer
      title={
        <Space>
          <LinkOutlined />
          <span>相关图纸 · 平面索引</span>
        </Space>
      }
      width={960}
      open={open}
      onClose={onClose}
      styles={{ body: { paddingTop: 8 } }}
    >
      {notReady && (
        <Alert
          type="info"
          showIcon
          message="索引尚未生成"
          description="需要 job 至少跑到 space_detect / plan_target_binding 后才有数据"
        />
      )}

      {/* 顶部统计 + 图纸清单 */}
      {drawingSet && (
        <>
          <Space size="large" wrap style={{ marginBottom: 16 }}>
            <Statistic
              title="索引总数"
              value={stats?.total_refs ?? 0}
              valueStyle={{ fontSize: 20 }}
            />
            <Statistic
              title="命中目标图"
              value={stats?.matched ?? 0}
              valueStyle={{ fontSize: 20, color: '#52c41a' }}
            />
            <Statistic
              title="未命中"
              value={stats?.unmatched ?? 0}
              valueStyle={{
                fontSize: 20,
                color: (stats?.unmatched ?? 0) > 0 ? '#ff4d4f' : undefined,
              }}
            />
            <Statistic
              title="锚点未落空间"
              value={stats?.orphan_anchors ?? 0}
              valueStyle={{
                fontSize: 20,
                color: (stats?.orphan_anchors ?? 0) > 0 ? '#faad14' : undefined,
              }}
            />
            <Statistic
              title="命中率"
              value={
                stats?.total_refs
                  ? Math.round(((stats.matched ?? 0) / stats.total_refs) * 1000) / 10
                  : 0
              }
              suffix="%"
              valueStyle={{ fontSize: 20 }}
            />
          </Space>

          {/* 图纸清单：按 role 打 Tag，方便复核跳转 */}
          <div style={{ marginBottom: 20 }}>
            <Text strong style={{ marginRight: 8 }}>
              已上传图纸：
            </Text>
            <Space size={[6, 6]} wrap>
              {drawingSet.drawings.map((d: Drawing) => (
                <Tooltip
                  key={d.id}
                  title={
                    <>
                      <div>类型：{d.drawing_type}</div>
                      <div>Sheet: {d.sheet_nos.join(', ') || '未识别'}</div>
                      {d.sheet_title && <div>标题：{d.sheet_title}</div>}
                    </>
                  }
                >
                  <Tag color={DRAWING_ROLE_COLORS[d.role] || 'default'}>
                    {DRAWING_ROLE_LABELS[d.role] || d.role} ·{' '}
                    {d.sheet_nos.length > 0
                      ? d.sheet_nos[0] + (d.sheet_nos.length > 1 ? `~${d.sheet_nos[d.sheet_nos.length - 1]}` : '')
                      : '?'}
                    {d.filename ? `  (${d.filename})` : ''}
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          </div>
        </>
      )}

      {/* 操作栏 */}
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Segmented
          value={tab}
          onChange={(v) => setTab(v as IndexKind | 'all')}
          options={KIND_TABS.map((k) => ({
            label: (
              <span>
                {k === 'all' ? '全部' : INDEX_KIND_LABELS[k]}
                <Badge
                  count={countByKind[k] || 0}
                  size="small"
                  style={{ marginLeft: 6, backgroundColor: '#e6e6e6', color: '#333' }}
                  showZero
                />
              </span>
            ),
            value: k,
          }))}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingRef(null)
            setModalOpen(true)
          }}
        >
          手工新增
        </Button>
      </Space>

      {/* 索引表格 */}
      {refsSet && refsSet.refs.length === 0 ? (
        <Empty description="未识别到平面索引；可手工新增" />
      ) : (
        <Table<PlanIndexRef>
          dataSource={filteredRefs}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 20 }}
          columns={[
            {
              title: '类型',
              dataIndex: 'kind',
              width: 90,
              render: (k: IndexKind) => (
                <Tag>{INDEX_KIND_LABELS[k]}</Tag>
              ),
              filters: KIND_TABS.filter((k) => k !== 'all').map((k) => ({
                text: INDEX_KIND_LABELS[k as IndexKind],
                value: k,
              })),
              onFilter: (v, r) => r.kind === v,
            },
            {
              title: '编号 / 图号',
              width: 130,
              render: (_, r) => (
                <span>
                  <Text code>{r.view_no}</Text>
                  {r.sheet_no ? (
                    <>
                      {' / '}
                      <Text code>{r.sheet_no}</Text>
                    </>
                  ) : null}
                </span>
              ),
            },
            {
              title: '空间',
              dataIndex: 'attached_space_key',
              width: 90,
              render: (sp: string | null) =>
                sp ? <Tag color="blue">{sp}</Tag> : <Text type="warning">未落</Text>,
            },
            {
              title: '目标图',
              width: 320,
              render: (_, r) => {
                const b = bindingByRef[r.id]
                if (!b) return <Text type="secondary">-</Text>
                if (!b.matched) {
                  return (
                    <Tag color="red" icon={<WarningOutlined />}>
                      未命中（{b.reason}）
                    </Tag>
                  )
                }
                return (
                  <Space size={4} wrap>
                    <Tag color="green">
                      {DRAWING_ROLE_LABELS[b.target_drawing_type || ''] ||
                        b.target_drawing_type}
                    </Tag>
                    <Text ellipsis style={{ maxWidth: 220 }}>
                      {b.target_filename}
                    </Text>
                  </Space>
                )
              },
            },
            {
              title: '识别方式',
              dataIndex: 'detected_by',
              width: 110,
              render: (v: string) =>
                v === 'manual' ? (
                  <Tag color="purple">手工</Tag>
                ) : v.startsWith('insert') ? (
                  <Tag color="geekblue">块</Tag>
                ) : v.startsWith('geometry') ? (
                  <Tag color="cyan">几何</Tag>
                ) : (
                  <Tag>{v}</Tag>
                ),
            },
            {
              title: '锚点 (m)',
              width: 130,
              render: (_, r) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {r.anchor_xy[0].toFixed(2)}, {r.anchor_xy[1].toFixed(2)}
                </Text>
              ),
            },
            {
              title: '操作',
              width: 110,
              render: (_, r) => (
                <Space size={4}>
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingRef(r)
                      setModalOpen(true)
                    }}
                  >
                    编辑
                  </Button>
                  <Popconfirm
                    title="确认删除此索引？"
                    onConfirm={() => deleteMutation.mutate(r.id)}
                  >
                    <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                      删
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      )}

      <UpsertModal
        jobId={jobId}
        open={modalOpen}
        drawingSet={drawingSet}
        editing={editingRef}
        onCancel={() => {
          setModalOpen(false)
          setEditingRef(null)
        }}
        onSubmit={(body) => upsertMutation.mutate({ jobId, ...body } as never)}
        submitting={upsertMutation.isPending}
      />
    </Drawer>
  )
}

// ============================================================================
// 新增 / 编辑 索引 Modal
// ============================================================================

function UpsertModal({
  jobId: _jobId,
  open,
  editing,
  drawingSet,
  onCancel,
  onSubmit,
  submitting,
}: {
  jobId: UUID
  open: boolean
  editing: PlanIndexRef | null
  drawingSet: { drawings: Drawing[] } | undefined
  onCancel: () => void
  onSubmit: (body: {
    kind: IndexKind
    view_no: string
    sheet_no?: string
    anchor_xy: [number, number]
    ref_id?: UUID
  }) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()

  // 收集所有可选 sheet_no（用于下拉提示）
  const allSheetNos = useMemo(() => {
    if (!drawingSet) return []
    const nos = new Set<string>()
    drawingSet.drawings.forEach((d) => d.sheet_nos.forEach((s) => nos.add(s)))
    return Array.from(nos).sort()
  }, [drawingSet])

  return (
    <Modal
      title={editing ? '编辑索引' : '手工新增索引'}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okText="保存"
      cancelText="取消"
      confirmLoading={submitting}
      destroyOnClose
      width={560}
    >
      <Alert
        icon={<InfoCircleOutlined />}
        showIcon
        type="info"
        message="锚点坐标 (m)"
        description="用米制。可参考「预览」上目标点的坐标手工填入。V2 会支持点击预览图直接选点。"
        style={{ marginBottom: 12 }}
      />
      <Form
        form={form}
        layout="vertical"
        initialValues={
          editing
            ? {
                kind: editing.kind,
                view_no: editing.view_no,
                sheet_no: editing.sheet_no,
                x: editing.anchor_xy[0],
                y: editing.anchor_xy[1],
              }
            : { kind: 'elevation' as IndexKind }
        }
        onFinish={(v) =>
          onSubmit({
            kind: v.kind,
            view_no: v.view_no,
            sheet_no: v.sheet_no || undefined,
            anchor_xy: [Number(v.x), Number(v.y)],
            ref_id: editing?.id,
          })
        }
      >
        <Form.Item name="kind" label="类型" rules={[{ required: true }]}>
          <Select
            options={(Object.keys(INDEX_KIND_LABELS) as IndexKind[]).map((k) => ({
              value: k,
              label: INDEX_KIND_LABELS[k],
            }))}
          />
        </Form.Item>
        <Form.Item
          name="view_no"
          label="视图编号（如 01 / A / M01）"
          rules={[{ required: true, message: '必填' }]}
        >
          <Input placeholder="01" />
        </Form.Item>
        <Form.Item name="sheet_no" label="目标图号（如 022 / DY03）">
          <Select
            allowClear
            showSearch
            placeholder="留空表示暂不指定目标图"
            options={allSheetNos.map((s) => ({ value: s, label: s }))}
          />
        </Form.Item>
        <Form.Item label="锚点坐标">
          <Space>
            <Form.Item name="x" noStyle rules={[{ required: true }]}>
              <InputNumber placeholder="X (m)" style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="y" noStyle rules={[{ required: true }]}>
              <InputNumber placeholder="Y (m)" style={{ width: 140 }} />
            </Form.Item>
          </Space>
        </Form.Item>
      </Form>

      {editing && (
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="原识别">
            {editing.detected_by} · 置信度 {(editing.confidence * 100).toFixed(0)}%
          </Descriptions.Item>
        </Descriptions>
      )}
    </Modal>
  )
}
